// Spec §41: profile + delete account.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { userApi } from '@/api/userApi';
import { ApiException } from '@/api/errors';
import { formatDate } from '@/utils/format';
import type { UserProfile } from '@/api/types';

export function ProfileScreen() {
  const { session, getToken, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bio, setBio] = useState('');
  const [username, setUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBusy(true);
      try {
        const me = await userApi.me(getToken);
        if (cancelled) return;
        setProfile(me);
        setBio(me.bio ?? '');
        setUsername(me.username);
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load your profile.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  async function handleSave(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await userApi.updateProfile(getToken, { bio, username });
      setProfile(updated);
      setSuccess(true);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Soft-delete your account? You can sign up again with the same email.'))
      return;
    try {
      await userApi.deleteMe(getToken);
      await logout();
      navigate('/login', { replace: true });
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not delete your account.');
    }
  }

  if (busy) return <div className="empty">Loading profile…</div>;

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Profile</h2>
        <div className="muted" style={{ fontSize: 13 }}>
          Signed in as <strong>{session?.email}</strong> · joined{' '}
          {formatDate(profile?.created_at ?? null)}
        </div>
        {error ? <div className="error-banner">{error}</div> : null}
        {success ? <div className="info-banner">Profile saved.</div> : null}
        <form className="stack" onSubmit={handleSave} noValidate>
          <div className="field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={3}
              maxLength={32}
            />
          </div>
          <div className="field">
            <label htmlFor="bio">Bio</label>
            <textarea
              id="bio"
              className="textarea"
              style={{ minHeight: 120 }}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
            />
          </div>
          <div className="row">
            <button className="btn btn--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Delete account</h3>
        <p className="muted">
          Soft-deletes your account. Your reports and photos stay archived; you can sign up again
          with the same email to reactivate.
        </p>
        <button className="btn btn--danger" type="button" onClick={handleDelete}>
          Delete account
        </button>
      </div>
    </div>
  );
}
